import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  DispatchSnapshotResult,
  SnapshotActionId,
  SnapshotRun,
  SnapshotStatus,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const snapshotKeys = {
  status: ['snapshot', 'status'] as const,
  runs: ['snapshot', 'runs'] as const,
};

/** Live snapshot status (environment, sandbox URLs, online, dispatch-configured). */
export function useSnapshotStatus(enabled = true): UseQueryResult<SnapshotStatus> {
  return useQuery({
    queryKey: snapshotKeys.status,
    queryFn: () => api.get<SnapshotStatus>('/snapshot/status'),
    enabled,
    refetchInterval: 20_000,
  });
}

/** Recent runs of the snapshot workflows. */
export function useSnapshotRuns(enabled = true): UseQueryResult<SnapshotRun[]> {
  return useQuery({
    queryKey: snapshotKeys.runs,
    queryFn: () => api.get<SnapshotRun[]>('/snapshot/runs'),
    enabled,
    refetchInterval: 15_000,
  });
}

/** Dispatch a snapshot action (refresh / redeploy / stop / start). */
export function useDispatchSnapshotAction(): UseMutationResult<
  DispatchSnapshotResult,
  unknown,
  SnapshotActionId
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: SnapshotActionId) =>
      api.post<DispatchSnapshotResult>(`/snapshot/actions/${action}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: snapshotKeys.runs });
      void queryClient.invalidateQueries({ queryKey: snapshotKeys.status });
    },
  });
}
